/**
 * Indicadores que dependem dos registros novos da #6509:
 *   - titularidade em até 3 dias úteis (Brenda): do termo assinado ao protocolo na Equatorial;
 *   - custo de ferramentas do mês (Felipe), contra o orçamento;
 *   - gasto contra a verba (mídia e ferramentas), com a projeção do mês.
 * O erro e retrabalho do Faturamento está em src/lib/motivosCancelamentoBoleto.ts e a retenção
 * em src/lib/resultadoCancelamento.ts.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/registrosFase3.test.ts
 */
import { somarDiasUteis } from '../previsaoFaturaEquatorial.ts';

// ─────────────────────────── Titularidade ───────────────────────────

export const DU_PRAZO_PROTOCOLO = 3;
export const META_TITULARIDADE_NO_PRAZO = 0.95;

export interface ProtocoloDaTroca {
  id: number;
  protocolo_titularidade: string | null;
  titularidade_protocolada_em: string | null;
  protocolo_saldo: string | null;
  saldo_protocolado_em: string | null;
  saldo_nao_se_aplica: boolean | null;
}

const diaLocal = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Último dia ('AAAA-MM-DD') para protocolar: 3 dias úteis depois da assinatura, com feriados. */
export const prazoDoProtocolo = (assinadoEm: string): string => somarDiasUteis(diaLocal(assinadoEm), DU_PRAZO_PROTOCOLO);

export interface TitularidadeNoPrazo {
  /** Assinadas no período. */
  assinadas: number;
  noPrazo: number;
  /** Protocoladas depois do prazo, ou sem protocolo com o prazo vencido. */
  foraDoPrazo: number;
  /** Sem protocolo e ainda dentro do prazo: não entram na conta. */
  aguardando: number;
  taxa: number | null;
}

/**
 * Trocas assinadas entre `inicio` (incluso) e `fim` (exclusivo): quantas foram protocoladas na
 * Equatorial em até 3 dias úteis. A que ainda está no prazo sem protocolo fica fora da conta.
 */
export function titularidadeNoPrazo(
  trocas: readonly { assinado_em: string | null; protocolo: Pick<ProtocoloDaTroca, 'titularidade_protocolada_em'> | null }[],
  inicio: Date,
  fim: Date,
  hojeIso: string,
): TitularidadeNoPrazo {
  const r: TitularidadeNoPrazo = { assinadas: 0, noPrazo: 0, foraDoPrazo: 0, aguardando: 0, taxa: null };
  for (const t of trocas) {
    if (!t.assinado_em) continue;
    const ms = Date.parse(t.assinado_em);
    if (!(ms >= inicio.getTime() && ms < fim.getTime())) continue;
    r.assinadas++;
    const prazo = prazoDoProtocolo(t.assinado_em);
    const protocolada = t.protocolo?.titularidade_protocolada_em?.slice(0, 10) ?? null;
    if (protocolada) {
      if (protocolada <= prazo) r.noPrazo++; else r.foraDoPrazo++;
    } else if (hojeIso > prazo) r.foraDoPrazo++;
    else r.aguardando++;
  }
  const decididas = r.noPrazo + r.foraDoPrazo;
  r.taxa = decididas ? r.noPrazo / decididas : null;
  return r;
}

// ─────────────────────────── Ferramentas ───────────────────────────

export type AreaDaFerramenta = 'tecnologia' | 'marketing';

export interface Ferramenta {
  id: number;
  nome: string;
  area: AreaDaFerramenta;
  valor_mensal: number | string;
  /** 'AAAA-MM-DD' (dia 1 do primeiro mês cobrado). */
  inicio: string;
  /** Último mês cobrado; null = ativa. */
  fim: string | null;
  observacao: string | null;
}

/** Ferramentas cobradas no mês 'AAAA-MM' e a soma, por área. */
export function custoDasFerramentas(ferramentas: readonly Ferramenta[], mes: string) {
  const alvo = `${mes.slice(0, 7)}-01`;
  const doMes = ferramentas
    .filter(f => f.inicio.slice(0, 7) <= mes.slice(0, 7) && (!f.fim || f.fim.slice(0, 7) >= mes.slice(0, 7)))
    .map(f => ({ ...f, valor: Number(f.valor_mensal) || 0 }))
    .sort((a, b) => b.valor - a.valor || a.nome.localeCompare(b.nome));
  const soma = (area?: AreaDaFerramenta) => doMes.filter(f => !area || f.area === area).reduce((s, f) => s + f.valor, 0);
  return { mes: alvo, ferramentas: doMes, total: soma(), tecnologia: soma('tecnologia'), marketing: soma('marketing') };
}

// ─────────────────────────── Gasto contra a verba ───────────────────────────

export interface ContraOOrcamento {
  gasto: number;
  orcamento: number | null;
  /** Gasto ÷ orçamento. */
  uso: number | null;
  /** No ritmo de até hoje, quanto o mês fecha (mês fechado: o próprio gasto). */
  projecao: number;
  projecaoUso: number | null;
  estourou: boolean;
}

/** `fracao` = quanto do mês já passou (1 = mês fechado). Custo fixo (ferramentas) não se projeta. */
export function contraOOrcamento(gasto: number, orcamento: number | null, fracao: number, { projetar = true } = {}): ContraOOrcamento {
  const projecao = projetar && fracao > 0 && fracao < 1 ? gasto / fracao : gasto;
  const tem = orcamento != null && orcamento > 0;
  return {
    gasto,
    orcamento: tem ? orcamento : null,
    uso: tem ? gasto / orcamento! : null,
    projecao,
    projecaoUso: tem ? projecao / orcamento! : null,
    estourou: tem ? projecao > orcamento! : false,
  };
}
